import { createPluginRegistration } from '@embedpdf/core'
import { EmbedPDF } from '@embedpdf/core/react'
import { usePdfiumEngine } from '@embedpdf/engines/react'
import { useTranslation } from 'react-i18next'
import { OcrService } from '../../services/ocr/ocr-service'

// Essential Plugins
import { ViewportPluginPackage, Viewport } from '@embedpdf/plugin-viewport/react'
import { ScrollPluginPackage, Scroller, useScroll } from '@embedpdf/plugin-scroll/react'
import { ZoomPluginPackage, ZoomMode, ZoomGestureWrapper } from '@embedpdf/plugin-zoom/react'
import { RenderPluginPackage, RenderLayer } from '@embedpdf/plugin-render/react'
import { DocumentManagerPluginPackage, DocumentContent } from '@embedpdf/plugin-document-manager/react'
import { InteractionManagerPluginPackage, PagePointerProvider, GlobalPointerProvider } from '@embedpdf/plugin-interaction-manager/react'

// Feature Plugins
import { AnnotationPluginPackage, AnnotationLayer, useAnnotation, LockModeType } from '@embedpdf/plugin-annotation/react'
import { FormPluginPackage } from '@embedpdf/plugin-form/react'
import { CapturePluginPackage, MarqueeCapture, useCapture } from '@embedpdf/plugin-capture/react'
import { ExportPluginPackage } from '@embedpdf/plugin-export/react'
import { PanPluginPackage, usePan } from '@embedpdf/plugin-pan/react'
import { PrintPluginPackage } from '@embedpdf/plugin-print/react'
import { RedactionPluginPackage, RedactionLayer } from '@embedpdf/plugin-redaction/react'
import { RotatePluginPackage } from '@embedpdf/plugin-rotate/react'
import { StampPluginPackage } from '@embedpdf/plugin-stamp/react'
import { SearchPluginPackage, SearchLayer } from '@embedpdf/plugin-search/react'
import { SelectionPluginPackage, SelectionLayer } from '@embedpdf/plugin-selection/react'
import { SpreadPluginPackage } from '@embedpdf/plugin-spread/react'
import { ThumbnailPluginPackage } from '@embedpdf/plugin-thumbnail/react'
import { HistoryPluginPackage } from '@embedpdf/plugin-history/react'
import { TilingPluginPackage, TilingLayer } from '@embedpdf/plugin-tiling/react'

import { Loader2 } from 'lucide-react'
import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { TabBar } from './TabBar'
import { PrimaryToolbar } from './PrimaryToolbar'
import { ContextualToolbar } from './ContextualToolbar'
import { Sidebar } from './Sidebar'
import { RightPanel } from './RightPanel'
import { ViewerMode } from './ViewerModeTabs'
import { AnnotationDrawLayer } from './AnnotationDrawLayer'
import { AnnotationSelectionMenu } from './AnnotationSelectionMenu'
import { TextSelectionMenu } from './SelectionMenu'
import { RedactionSelectionMenu } from './RedactionSelectionMenu'
import { OcrResultsOverlay } from './ocr/OcrResultsOverlay'
import { OcrDownloadModal } from './ocr/OcrDownloadModal'
import { FeedbackModal, shouldShow as shouldShowFeedback } from './FeedbackModal'
import { unsavedChangesRef } from './ContextualToolbar'
import { PageNavigator } from './PageNavigator'
import { DocumentRestorer } from './DocumentRestorer'
import { LaunchFileHandler } from './LaunchFileHandler'
import { Dashboard } from './Dashboard'
import type { OcrResult } from '../../services/ocr/ocr-types'
import { usePersistedState } from '../../hooks/usePersistedState'
import { CommandsPluginPackage } from '@embedpdf/plugin-commands/react'
import type { Command } from '@embedpdf/plugin-commands'
import { saveDocument } from '../../services/indexeddb-service'
import { writeToFile } from '../../services/file-handle-service'
import { PdfAnnotationName } from '@embedpdf/models'

export type RightPanelMode = 'comments' | 'search' | 'ocrResults' | 'dictionary'

function getPluginApi(registry: any, pluginId: string): any {
  const p = registry.getPlugin(pluginId)
  if (!p) return null
  return p.provides()
}

const commands: Record<string, Command> = {
  'file.save': {
    id: 'file.save',
    label: 'Save',
    shortcuts: ['mod+s'],
    action: ({ registry, documentId }) => {
      const exportApi = getPluginApi(registry, 'export')
      const docManager = getPluginApi(registry, 'document-manager')
      if (!exportApi || !docManager) return
      const task = exportApi.saveAsCopy()
      if (!task) return
      const docMeta = docManager.getDocumentState(documentId)
      const docName = docMeta?.name || docMeta?.id || 'document.pdf'
      task.wait(async (buffer: ArrayBuffer) => {
        const wrote = await writeToFile(buffer)
        if (!wrote) {
          saveDocument({
            id: 'current-document',
            name: docName,
            buffer,
            byteLength: buffer.byteLength,
            currentPage: 0,
            lastOpened: Date.now(),
          }).catch(() => {})
        }
      }, () => {})
    },
  },
  'file.saveAs': {
    id: 'file.saveAs',
    label: 'Save As',
    shortcuts: ['mod+shift+s'],
    action: ({ registry }) => {
      const exportApi = getPluginApi(registry, 'export')
      if (!exportApi) return
      const task = exportApi.saveAsCopy()
      if (!task) return
      task.wait(async (buffer: ArrayBuffer) => {
        let name = 'document.pdf'
        try {
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: 'document.pdf',
            types: [{ description: 'PDF Files', accept: { 'application/pdf': ['.pdf'] } }],
          })
          name = handle.name
          const writable = await handle.createWritable()
          await writable.write(buffer)
          await writable.close()
        } catch (err: any) {
          if (err?.name === 'AbortError') return
          name = 'document.pdf'
          const blob = new Blob([buffer], { type: 'application/pdf' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = name.endsWith('.pdf') ? name : `${name}.pdf`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }
      }, () => {})
    },
  },
  'file.print': {
    id: 'file.print',
    label: 'Print',
    shortcuts: ['mod+p'],
    action: ({ registry }) => {
      const printApi = getPluginApi(registry, 'print')
      printApi?.print({ includeAnnotations: true })
    },
  },
  'file.open': {
    id: 'file.open',
    label: 'Open',
    shortcuts: ['mod+o'],
    action: () => {
      document.getElementById('tab-bar-file-input')?.click()
    },
  },
  'edit.undo': {
    id: 'edit.undo',
    label: 'Undo',
    shortcuts: ['mod+z'],
    action: ({ registry }) => {
      const historyApi = getPluginApi(registry, 'history')
      historyApi?.undo()
    },
  },
  'edit.redo': {
    id: 'edit.redo',
    label: 'Redo',
    shortcuts: ['mod+shift+z', 'mod+y'],
    action: ({ registry }) => {
      const historyApi = getPluginApi(registry, 'history')
      historyApi?.redo()
    },
  },
  'view.zoomIn': {
    id: 'view.zoomIn',
    label: 'Zoom In',
    shortcuts: ['mod+equals', 'mod+numadd'],
    action: ({ registry }) => {
      const zoomApi = getPluginApi(registry, 'zoom')
      zoomApi?.zoomIn()
    },
  },
  'view.zoomOut': {
    id: 'view.zoomOut',
    label: 'Zoom Out',
    shortcuts: ['mod+minus', 'mod+numsub'],
    action: ({ registry }) => {
      const zoomApi = getPluginApi(registry, 'zoom')
      zoomApi?.zoomOut()
    },
  },
  'view.fitPage': {
    id: 'view.fitPage',
    label: 'Fit to Page',
    shortcuts: ['mod+0', 'mod+num0'],
    action: ({ registry }) => {
      const zoomApi = getPluginApi(registry, 'zoom')
      zoomApi?.requestZoom(ZoomMode.FitPage)
    },
  },
  'view.fitWidth': {
    id: 'view.fitWidth',
    label: 'Fit to Width',
    shortcuts: ['mod+1'],
    action: ({ registry }) => {
      const zoomApi = getPluginApi(registry, 'zoom')
      zoomApi?.requestZoom(ZoomMode.FitWidth)
    },
  },
  'nav.previousPage': {
    id: 'nav.previousPage',
    label: 'Previous Page',
    shortcuts: ['arrowleft'],
    action: ({ registry }) => {
      const scrollApi = getPluginApi(registry, 'scroll')
      scrollApi?.scrollToPreviousPage('smooth')
    },
  },
  'nav.nextPage': {
    id: 'nav.nextPage',
    label: 'Next Page',
    shortcuts: ['arrowright'],
    action: ({ registry }) => {
      const scrollApi = getPluginApi(registry, 'scroll')
      scrollApi?.scrollToNextPage('smooth')
    },
  },
  'annotate.delete': {
    id: 'annotate.delete',
    label: 'Delete Annotation',
    shortcuts: ['delete', 'backspace'],
    action: ({ registry }) => {
      const annotationApi = getPluginApi(registry, 'annotation')
      if (!annotationApi) return
      const selection = annotationApi.getSelectedAnnotation()
      if (selection) {
        annotationApi.deleteAnnotation(selection.object.pageIndex, selection.object.id)
      }
    },
  },
  'annotate.escape': {
    id: 'annotate.escape',
    label: 'Deselect',
    shortcuts: ['escape'],
    action: ({ registry }) => {
      const annotationApi = getPluginApi(registry, 'annotation')
      annotationApi?.setActiveTool(null)
      annotationApi?.deselectAnnotation()
    },
  },
}

const plugins = [
  createPluginRegistration(DocumentManagerPluginPackage, {}),
  createPluginRegistration(ViewportPluginPackage),
  createPluginRegistration(ScrollPluginPackage),
  createPluginRegistration(RenderPluginPackage),
  createPluginRegistration(ZoomPluginPackage, {
    defaultZoomLevel: ZoomMode.FitPage,
  }),
  // Annotation dependencies — must be registered BEFORE the annotation plugin
  createPluginRegistration(InteractionManagerPluginPackage),
  createPluginRegistration(SelectionPluginPackage),
  createPluginRegistration(HistoryPluginPackage),
  createPluginRegistration(AnnotationPluginPackage, {
    annotationAuthor: 'User',
    autoCommit: true,
    deactivateToolAfterCreate: false,
    locked: { type: LockModeType.Include, categories: ['form'] },
  }),
  createPluginRegistration(FormPluginPackage),
  createPluginRegistration(CapturePluginPackage),
  createPluginRegistration(ExportPluginPackage),
  createPluginRegistration(PanPluginPackage, {
    defaultMode: 'never',
  }),
  createPluginRegistration(PrintPluginPackage),
  createPluginRegistration(RedactionPluginPackage, {
    useAnnotationMode: true,
  }),
  createPluginRegistration(RotatePluginPackage),
  createPluginRegistration(StampPluginPackage, {
    libraries: [{
      id: 'custom-stamps',
      name: 'Custom Stamps',
      pdf: '/stamps/stamp.pdf',
      stamps: [
        { id: 's1', pageIndex: 0, name: PdfAnnotationName.Comment, subject: 'Stamp 1' },
        { id: 's2', pageIndex: 1, name: PdfAnnotationName.Approved, subject: 'Stamp 2' },
        { id: 's3', pageIndex: 2, name: PdfAnnotationName.Final, subject: 'Stamp 3' },
        { id: 's4', pageIndex: 3, name: PdfAnnotationName.Confidential, subject: 'Stamp 4' },
      ],
      categories: ['custom', 'sidebar'],
    }],
    defaultLibrary: {
      id: 'opakor-stamps',
      name: 'OpakorPDF Stamps',
      categories: ['custom', 'sidebar'],
    },
  }),
  createPluginRegistration(SearchPluginPackage),
  createPluginRegistration(SpreadPluginPackage),
  createPluginRegistration(ThumbnailPluginPackage),
  createPluginRegistration(TilingPluginPackage),
  createPluginRegistration(CommandsPluginPackage, { commands }),
]

interface ViewerContentProps {
  documentId: string
  activeMode: ViewerMode
  setIsRightPanelOpen: (open: boolean) => void
  setRightPanelMode: (mode: RightPanelMode) => void
}

const ViewerContent = ({ documentId, activeMode, setIsRightPanelOpen, setRightPanelMode }: ViewerContentProps) => {
  const { isPanning } = usePan(documentId)
  const { state: captureState } = useCapture(documentId)
  const { provides: annotationApi, state: annotationState } = useAnnotation(documentId)
  const { provides: scrollApi } = useScroll(documentId)

  const isMarqueeActive = captureState?.isMarqueeCaptureActive ?? false
  const activeToolId = annotationState?.activeToolId

  const [ocrResults, setOcrResults] = useState<OcrResult[]>([])
  const [ocrPageIndex, setOcrPageIndex] = useState<number | null>(null)
  const [ocrPageWidth, setOcrPageWidth] = useState<number | null>(null)
  const [ocrPageHeight, setOcrPageHeight] = useState<number | null>(null)
  const [isAreaOcr, setIsAreaOcr] = useState(false)

  useEffect(() => {
    const onUpdate = () => {
      setOcrResults([...OcrService.results])
      setOcrPageIndex(OcrService.pageIndex)
      setOcrPageWidth(OcrService.pageWidth)
      setOcrPageHeight(OcrService.pageHeight)
      setIsAreaOcr(OcrService.isAreaOcr)
    }
    onUpdate()
    return OcrService.subscribe(onUpdate)
  }, [])

  const handleOcrClose = useCallback(() => {
    OcrService.dismissOverlay()
  }, [])

  const cursorClass = useMemo(() => {
    let result = '';
    if (activeMode === 'View') {
      if (isPanning) result = 'view-cursor-pan'
      else if (isMarqueeActive) result = 'cursor-crosshair'
      else result = 'view-cursor-selection'
    } else if (activeMode === 'Annotate' || activeMode === 'Shapes' || activeMode === 'Redact') {
      if (activeToolId) {
        const textCursors = new Set(['highlight', 'strikeout', 'underline', 'squiggly', 'freeText', 'stamp'])
        result = textCursors.has(activeToolId) ? 'annotation-cursor-text' : 'annotation-cursor-crosshair'
      }
    }
    
    return result;
  }, [activeMode, isPanning, isMarqueeActive, activeToolId])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const annoEl = target.closest('[data-no-interaction]')
      if (!annoEl || !annotationApi) return
      const selectedIds = annotationApi.getSelectedAnnotationIds()
      if (selectedIds.length === 0) return
      const selectedId = selectedIds[0]
      if (!selectedId) return
      const state = annotationApi.getState()
      const tracked = state.byUid[selectedId]
      if (!tracked || tracked.object.type !== 1) return
      setRightPanelMode('comments')
      setIsRightPanelOpen(true)
      scrollApi?.scrollToPage({ pageNumber: tracked.object.pageIndex + 1, behavior: 'auto' })
    }
    document.addEventListener('dblclick', handler)
    return () => document.removeEventListener('dblclick', handler)
  }, [annotationApi, scrollApi, setRightPanelMode, setIsRightPanelOpen])

  return (
    <DocumentContent documentId={documentId}>
      {({ isLoading, isError, isLoaded }) => (
        <>
          {isLoading && (
            <div className="flex-1 flex items-center justify-center bg-bg-app">
            <div className="flex flex-col items-center gap-3">
              <Loader2 size={32} className="animate-spin text-accent" />
              <span className="text-sm text-fg-muted">Loading document...</span>
            </div>
          </div>
        )}
        {isError && (
          <div className="flex-1 flex items-center justify-center bg-bg-app">
            <div className="flex flex-col items-center gap-3 text-center px-4">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 text-xl">!</div>
              <span className="text-sm text-fg-muted">Failed to load document</span>
            </div>
          </div>
        )}
        {isLoaded && (
        <GlobalPointerProvider documentId={documentId}>
          <Viewport
            documentId={documentId}
            className={`absolute inset-0 ${cursorClass}`}
            style={{ touchAction: 'none' }}
          >
            <ZoomGestureWrapper documentId={documentId} enablePinch>
              <Scroller
                documentId={documentId}
                renderPage={({ pageIndex }) => (
                    <PagePointerProvider
                      documentId={documentId}
                      pageIndex={pageIndex}
                      className="shadow-[0_0_50px_rgba(0,0,0,0.5)] relative"
                      id={`embedpdf-page-${pageIndex}`}
                    >
                      <RenderLayer
                        documentId={documentId}
                        pageIndex={pageIndex}
                        style={{ pointerEvents: 'none' }}
                      />
                      <TilingLayer
                        documentId={documentId}
                        pageIndex={pageIndex}
                        style={{ pointerEvents: 'none' }}
                      />
                      <SelectionLayer
                        documentId={documentId}
                        pageIndex={pageIndex}
                        selectionMenu={(props) => (
                          <TextSelectionMenu {...props} documentId={documentId} />
                        )}
                      />
                      <AnnotationLayer
                        documentId={documentId}
                        pageIndex={pageIndex}
                        selectionMenu={(props) => (
                          <AnnotationSelectionMenu {...props} documentId={documentId} />
                        )}
                      />
                      <SearchLayer
                        documentId={documentId}
                        pageIndex={pageIndex}
                        style={{ pointerEvents: 'none' }}
                      />
                      <AnnotationDrawLayer
                        documentId={documentId}
                        pageIndex={pageIndex}
                      />
                      <RedactionLayer
                        documentId={documentId}
                        pageIndex={pageIndex}
                        selectionMenu={(props) => (
                          <RedactionSelectionMenu documentId={documentId} {...props} />
                        )}
                      />
                      <MarqueeCapture
                        documentId={documentId}
                        pageIndex={pageIndex}
                      />
                      {!isAreaOcr && ocrResults.length > 0 && pageIndex === ocrPageIndex && ocrPageWidth != null && ocrPageHeight != null && (
                        <OcrResultsOverlay
                          results={ocrResults}
                          pageWidth={ocrPageWidth}
                          pageHeight={ocrPageHeight}
                          onClose={handleOcrClose}
                        />
                      )}
                    </PagePointerProvider>
                )}
              />
            </ZoomGestureWrapper>
          </Viewport>
        </GlobalPointerProvider>
        )}
        </>
      )}
    </DocumentContent>
  )
}

export const PDFViewer = () => {
  const { t } = useTranslation()
  const { engine, isLoading } = usePdfiumEngine()
  const [activeMode, setActiveMode] = usePersistedState<ViewerMode>('pref_activeMode', 'View')
  const [isSidebarOpen, setIsSidebarOpen] = usePersistedState('pref_sidebarOpen', true)
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false)
  const [rightPanelMode, setRightPanelMode] = useState<RightPanelMode>('comments')
  const [showOcrDownloadModal, setShowOcrDownloadModal] = useState(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)

  const handleUnsavedSave = useCallback(() => {
    setShowUnsavedModal(false)
    unsavedChangesRef.current = false
    window.dispatchEvent(new CustomEvent('opakor-force-save'))
    setTimeout(() => window.location.reload(), 800)
  }, [])

  const handleUnsavedDiscard = useCallback(() => {
    setShowUnsavedModal(false)
    unsavedChangesRef.current = false
    window.location.reload()
  }, [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F5' || (e.key === 'r' && (e.ctrlKey || e.metaKey))) {
        if (unsavedChangesRef.current) {
          e.preventDefault()
          setShowUnsavedModal(true)
        }
      }
    }
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [])

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (unsavedChangesRef.current) {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  useEffect(() => {
    if (shouldShowFeedback()) {
      setShowFeedbackModal(true)
    }
  }, [])

  useEffect(() => {
    const handler = () => setShowOcrDownloadModal(true)
    window.addEventListener('opakor-start-ocr-preload', handler)
    return () => window.removeEventListener('opakor-start-ocr-preload', handler)
  }, [])

  useEffect(() => {
    const handler = (_e: Event) => {
      setRightPanelMode('dictionary')
      setIsRightPanelOpen(true)
    }
    window.addEventListener('opakor-open-dictionary', handler)
    return () => window.removeEventListener('opakor-open-dictionary', handler)
  }, [setRightPanelMode, setIsRightPanelOpen])

  useEffect(() => {
    if (engine) {
      OcrService.setPdfEngine(engine)
    }
    return () => {
      OcrService.setPdfEngine(null)
    }
  }, [engine])

  if (isLoading || !engine) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg-app">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={40} className="animate-spin text-accent" />
          <span className="text-fg-muted font-medium tracking-widest text-xs uppercase">{t('loading.engine')}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen w-full bg-bg-app text-fg-primary overflow-hidden font-sans selection:bg-accent/30">
      <EmbedPDF engine={engine} plugins={plugins}>
        {({ activeDocumentId }) => {
          if (!activeDocumentId) {
            return (
              <>
                <DocumentRestorer />
                <LaunchFileHandler />
                <Dashboard />
              </>
            )
          }
          return (
            <>
              <PDFViewerInternal 
                documentId={activeDocumentId}
              activeMode={activeMode}
              setActiveMode={setActiveMode}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              isRightPanelOpen={isRightPanelOpen}
              setIsRightPanelOpen={setIsRightPanelOpen}
              rightPanelMode={rightPanelMode}
              setRightPanelMode={setRightPanelMode}
            />
            </>
          )
        }}
      </EmbedPDF>
      <pwa-install
        manifest-url="/manifest.webmanifest"
        install-description="ដំឡើងកម្មវិធីនេះដើម្បីធ្វើការគូសពន្យល់ ដោយមិនចាំបាច់អ៊ិនធឺណិត ការបំពេញទម្រង់ និង ការទាញយកអក្សរពីឯកសារស្កេនជាខ្មែរ និងអង់គ្លេស | Install for offline PDF annotation, form filling, and OCR on Khmer & English documents"
        use-local-storage
      />
      {showOcrDownloadModal && (
        <OcrDownloadModal onDone={() => setShowOcrDownloadModal(false)} />
      )}
      {showFeedbackModal && (
        <FeedbackModal onClose={() => setShowFeedbackModal(false)} />
      )}
      {showUnsavedModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50">
          <div className="bg-bg-surface rounded-2xl shadow-2xl border border-border-default p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-fg-primary mb-2">{t('app.unsavedChangesTitle') || 'Unsaved Changes'}</h3>
            <p className="text-sm text-fg-muted mb-6">{t('app.unsavedChangesRefresh')}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleUnsavedSave}
                className="w-full px-4 py-2.5 bg-accent text-white rounded-xl text-sm font-medium hover:bg-accent/90 transition-colors"
              >
                {t('app.saveAndReload')}
              </button>
              <button
                onClick={handleUnsavedDiscard}
                className="w-full px-4 py-2.5 bg-bg-surface-alt text-fg-primary rounded-xl text-sm font-medium hover:bg-interactive-hover transition-colors border border-border-default"
              >
                {t('app.discardAndReload')}
              </button>
              <button
                onClick={() => setShowUnsavedModal(false)}
                className="w-full px-4 py-2.5 text-fg-muted rounded-xl text-sm hover:text-fg-primary hover:bg-interactive-hover transition-colors"
              >
                {t('app.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const PDFViewerInternal = ({ 
  documentId, 
  activeMode, 
  setActiveMode, 
  isSidebarOpen, 
  setIsSidebarOpen, 
  isRightPanelOpen, 
  setIsRightPanelOpen,
  rightPanelMode,
  setRightPanelMode,
}: any) => {
  const { t } = useTranslation()
  const { provides: panApi, isPanning } = usePan(documentId)
  const { provides: annotationApi, state: annotationState } = useAnnotation(documentId)
  const { provides: captureApi, state: captureState } = useCapture(documentId)

  const activeToolId = annotationState?.activeToolId
  const isMarqueeActive = captureState?.isMarqueeCaptureActive ?? false
  const prevModeRef = useRef<ViewerMode>(activeMode)

  useEffect(() => {
    const prevMode = prevModeRef.current
    prevModeRef.current = activeMode

    if (activeMode !== 'View' || prevMode === 'View' || !panApi) return

    console.log('[PDFViewer] Entered View mode. Setting up text selection as default tool.')

    if (isPanning) {
      console.log('[PDFViewer] DISABLING Pan tool');
      panApi.disablePan()
    }

    if (isMarqueeActive && captureApi) {
      console.log('[PDFViewer] DISABLING Marquee tool');
      captureApi.disableMarqueeCapture()
    }

    if (activeToolId && annotationApi) {
      console.log('[PDFViewer] CLEARING active annotation tool');
      annotationApi.setActiveTool(null)
    }
  }, [activeMode])

  return (
    <>
      {/* Zone 1: Tab Bar */}
      <TabBar activeDocumentId={documentId} />

      {/* Zone 2: Primary Toolbar */}
      {documentId && (
        <PrimaryToolbar 
          documentId={documentId}
          activeMode={activeMode}
          onModeChange={setActiveMode}
          isSidebarOpen={isSidebarOpen}
          onSidebarToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          isRightPanelOpen={isRightPanelOpen}
          rightPanelMode={rightPanelMode}
          setRightPanelMode={setRightPanelMode}
          setIsRightPanelOpen={setIsRightPanelOpen}
        />
      )}

      {/* Zone 3: Contextual Toolbar */}
      {documentId && (
        <ContextualToolbar 
          documentId={documentId} 
          activeMode={activeMode}
          setIsRightPanelOpen={setIsRightPanelOpen}
          setRightPanelMode={setRightPanelMode}
        />
      )}
      
      <div className="flex-1 flex min-h-0 relative">
        {/* Zone 4 (Left): Collapsible Sidebar */}
        {documentId && (
          <Sidebar 
            documentId={documentId} 
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />
        )}
        
        {/* Zone 4 (Center): Main Workspace */}
        <div className="flex-1 relative overflow-hidden bg-bg-app flex flex-col">
          {documentId ? (
            <>
              <ViewerContent documentId={documentId} activeMode={activeMode} setIsRightPanelOpen={setIsRightPanelOpen} setRightPanelMode={setRightPanelMode} />
              <PageNavigator documentId={documentId} />
            </>
              ) : (
            <div className="flex h-full items-center justify-center flex-col gap-4">
              <div className="w-20 h-20 rounded-2xl bg-bg-surface flex items-center justify-center border border-border-default">
                <span className="text-3xl text-fg-muted/30">PDF</span>
              </div>
              <div className="text-center">
                <p className="text-fg-muted font-medium text-sm">{t('app.noDocumentOpen')}</p>
                <p className="text-fg-muted/50 text-xs mt-1">{t('app.dropHint')}</p>
              </div>
            </div>
          )}
        </div>
        {documentId && (
          <RightPanel
            isOpen={isRightPanelOpen}
            onClose={() => setIsRightPanelOpen(false)}
            documentId={documentId}
            mode={rightPanelMode}
          />
        )}
      </div>
    </>
  )
}
